import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Shuffle, Trash2 } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import { athensYmd } from '../../utils/athensDate';
import { DetailPanel, GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN, SELECT_CLS } from '../shared/listPage';
import { SheetField, SheetSection } from '../settings/sheet';
import {
  DRAWER_FORM_CLS, EMPTY_STORE_FORM, RequiredMark, SIDE_BTN, StoreFormFields, generateRandomPin,
} from '../superadmin/SuperAdminStoresTab';

/**
 * Support → «Νέο κατάστημα» (25/10/2026). Ίδια φόρμα με τον super admin (StoreFormFields, ίδιες προεπιλογές και
 * λειτουργίες — απόφαση χρήστη) χωρίς τον διακόπτη εκπαίδευσης, και με «Αλυσίδα» πρώτη. Ένα αίτημα: ο server γράφει
 * κατάστημα, διαχειριστή, αλυσίδα, token Oxygen και σειρές μαζί ή τίποτα, και μετά φέρνει τον κατάλογο της αλυσίδας
 * αμέσως. Μετά τη δημιουργία ανοίγει η κάρτα του καταστήματος στο «Στήσιμο».
 *
 * «Χρέωση και εξοπλισμός» (27/10/2026, απόφαση χρήστη): εγκατάσταση (σήμερα), κύκλος, συμφωνημένη τιμή και ο
 * εξοπλισμός που παραδόθηκε μπαίνουν στην ίδια συναλλαγή· οι χρεώσιμες λειτουργίες βγαίνουν από ό,τι ανοίχτηκε στη φόρμα.
 */

export interface SupportOrg {
  id: number;
  name: string;
  catalog_mode: string;
  store_count: number;
  member_count?: number;
  product_count: number;
  price_lists: Array<{ id: number; name: string }>;
  in_scheduler_scope: boolean;
}

export interface CreatedStore {
  id: number;
  name: string;
  organization_id: number | null;
  catalog: null | { pulled: boolean; applied?: number; errors?: string[]; pending?: number; reason?: string; error?: string };
  in_scheduler_scope: boolean;
}

const emptyManager = () => ({ first_name: '', last_name: '', email: '', phone: '', pin_code: generateRandomPin() });
interface HwLine { item: string; price: string; paid_amount: string }
const emptyBilling = () => ({ installed_at: athensYmd(), billing_cycle: 'monthly', agreed_price: '', hardware: [] as HwLine[] });
const CYCLES: Array<[string, string]> = [
  ['monthly', 'Μηνιαίος'], ['bimonthly', 'Διμηνιαίος'], ['quarterly', 'Τριμηνιαίος'], ['semiannual', 'Εξαμηνιαίος'], ['annual', 'Ετήσιος'],
];
const AMOUNT_RE = /^\d+([.,]\d{1,2})?$/;
const amountOk = (v: string) => v.trim() === '' || AMOUNT_RE.test(v.trim());

const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.message || fallback;

const SupportNewStore: React.FC<{ open: boolean; onClose: () => void; onCreated: (s: CreatedStore) => void }> = ({ open, onClose, onCreated }) => {
  const [data, setData] = useState<any>({ ...EMPTY_STORE_FORM });
  const [manager, setManager] = useState(emptyManager);
  const [billing, setBilling] = useState(emptyBilling);
  const [orgs, setOrgs] = useState<SupportOrg[] | null>(null);
  const [noOrgInScope, setNoOrgInScope] = useState(true);
  const [orgId, setOrgId] = useState('');
  const [priceListId, setPriceListId] = useState('default');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setData({ ...EMPTY_STORE_FORM });
    setManager(emptyManager());
    setBilling(emptyBilling());
    setOrgId(''); setPriceListId('default'); setError(null);
    (async () => {
      try {
        const d: any = await supportAPI.getOrganizations();
        setOrgs(d?.organizations || []);
        setNoOrgInScope(d?.no_org_in_scope !== false);
      } catch { setOrgs([]); }
    })();
  }, [open]);

  const org = orgs?.find(o => String(o.id) === orgId) || null;
  const inScope = org ? org.in_scheduler_scope : noOrgInScope;

  const validate = (): string | null => {
    if (!String(data.name || '').trim()) return 'Το όνομα του καταστήματος χρειάζεται.';
    if (!String(data.username || '').trim()) return 'Το όνομα σύνδεσης του καταστήματος χρειάζεται.';
    if (String(data.password || '').length < 6) return 'Ο κωδικός του καταστήματος θέλει τουλάχιστον 6 χαρακτήρες.';
    // Κατάστημα σε Oxygen χωρίς token δεν εκδίδει κανένα παραστατικό.
    if (data.ecr_provider === 'oxygen' && !String(data.oxygen_api_key || '').trim()) return 'Για το Oxygen χρειάζεται το API key.';
    if (!manager.first_name.trim() || !manager.last_name.trim()) return 'Όνομα και επώνυμο του διαχειριστή χρειάζονται.';
    if (!manager.email.trim()) return 'Το email του διαχειριστή χρειάζεται.';
    if (!/^\d{6}$/.test(manager.pin_code)) return 'Το PIN του διαχειριστή θέλει ακριβώς 6 ψηφία.';
    if (!amountOk(billing.agreed_price)) return 'Η συμφωνημένη τιμή δεν είναι ποσό (π.χ. 700 ή 49,90).';
    if (billing.hardware.some(h => h.item.trim() && (!amountOk(h.price) || !amountOk(h.paid_amount)))) return 'Μια τιμή εξοπλισμού δεν είναι ποσό.';
    return null;
  };

  const submit = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null);
    setCreating(true);
    try {
      const { training_mode_enabled, ...store } = data;
      const created = await supportAPI.createStore({
        ...store,
        organization_id: org ? org.id : null,
        price_list_id: org && priceListId !== 'default' ? Number(priceListId) : null,
        manager,
        billing: {
          installed_at: billing.installed_at || null,
          billing_cycle: billing.billing_cycle,
          agreed_price: billing.agreed_price.trim() || null,
          hardware: billing.hardware.filter(h => h.item.trim()),
        },
      }) as unknown as CreatedStore;
      onCreated(created);
    } catch (e: any) {
      setError(errText(e, 'Η δημιουργία απέτυχε.'));
    } finally {
      setCreating(false);
    }
  };

  const managerField = (key: 'first_name' | 'last_name' | 'email' | 'phone', label: React.ReactNode, type = 'text') => (
    <SheetField label={label} htmlFor={`support-new-manager-${key}`}>
      <input id={`support-new-manager-${key}`} type={type} value={manager[key]} autoComplete="one-time-code" className={INPUT_CLS}
        onChange={(e) => setManager(m => ({ ...m, [key]: e.target.value }))} />
    </SheetField>
  );

  return (
    <DetailPanel
      open={open}
      onClose={() => { if (!creating) onClose(); }}
      title="Νέο κατάστημα"
      subtitle="Κατάστημα, διαχειριστής, αλυσίδα και Oxygen μαζί — μετά η λίστα «Στήσιμο»"
      width="lg"
      testId="support-new-store"
      footerStart={error ? (
        <p role="alert" data-testid="support-new-store-error" className="text-sm text-red-700 dark:text-red-300 max-w-md">{error}</p>
      ) : undefined}
      footer={(
        <>
          <button type="button" className={GHOST_BTN} onClick={onClose} disabled={creating}>Άκυρο</button>
          <button type="button" className={PRIMARY_BTN} onClick={submit} disabled={creating} data-testid="support-new-store-submit">
            {creating
              ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Δημιουργία…</>
              : <><Plus className="w-4 h-4" aria-hidden="true" />Δημιουργία</>}
          </button>
        </>
      )}
    >
      <div className={DRAWER_FORM_CLS}>
        <SheetSection id="support-new-chain" title="Αλυσίδα" hint="Σε αλυσίδα ο κατάλογός της μπαίνει στο κατάστημα αμέσως, με τις τιμές του τιμοκαταλόγου.">
          <div className="xs-grid2">
            <SheetField label="Αλυσίδα του καταστήματος" htmlFor="support-new-org">
              <select id="support-new-org" className={SELECT_CLS} value={orgId} disabled={orgs === null}
                onChange={(e) => { setOrgId(e.target.value); setPriceListId('default'); }}>
                <option value="">Χωρίς αλυσίδα (ανεξάρτητο)</option>
                {(orgs || []).map(o => <option key={o.id} value={String(o.id)}>{o.name} · {o.store_count} καταστήματα</option>)}
              </select>
            </SheetField>
            {org && org.price_lists.length > 0 && (
              <SheetField label="Τιμοκατάλογος" htmlFor="support-new-price-list">
                <select id="support-new-price-list" className={SELECT_CLS} value={priceListId} onChange={(e) => setPriceListId(e.target.value)}>
                  <option value="default">Βασικός (οι τιμές του καταλόγου)</option>
                  {org.price_lists.map(pl => <option key={pl.id} value={String(pl.id)}>{pl.name}</option>)}
                </select>
              </SheetField>
            )}
          </div>
          {org && (
            <p className="xs-note" data-testid="support-new-chain-note">
              {org.catalog_mode === 'central' || org.price_lists.length > 0
                ? `Ο κατάλογος της αλυσίδας (${org.product_count} προϊόντα) μπαίνει στο κατάστημα με τη δημιουργία.`
                : 'Ανεξάρτητη αλυσίδα: το κατάστημα στήνει δικό του κατάλογο.'}
            </p>
          )}
          {!inScope && (
            <div className="mt-3">
              <Notice testId="support-new-scope">
                Ο server δεν τρέχει τις αυτόματες εργασίες (π.χ. επανάληψη παραστατικών) για {org ? 'αυτή την αλυσίδα' : 'καταστήματα χωρίς αλυσίδα'}.
                Πες το στον super admin πριν ανοίξει το κατάστημα.
              </Notice>
            </div>
          )}
        </SheetSection>

        <StoreFormFields data={data} setData={setData} mode="create" allowTrainingMode={false} />

        <SheetSection id="support-new-manager" title="Διαχειριστής καταστήματος" hint="Μπαίνει με το PIN του στις ρυθμίσεις του καταστήματος.">
          <div className="xs-grid2">
            {managerField('first_name', <>Όνομα<RequiredMark /></>)}
            {managerField('last_name', <>Επώνυμο<RequiredMark /></>)}
            {managerField('email', <>Email<RequiredMark /></>, 'email')}
            {managerField('phone', 'Τηλέφωνο')}
          </div>
          <SheetField label="PIN (6 ψηφία)" htmlFor="support-new-manager-pin" className="mt-4">
            <div className="flex gap-2 max-w-xs">
              <input id="support-new-manager-pin" type="text" inputMode="numeric" maxLength={6} value={manager.pin_code}
                onChange={(e) => setManager(m => ({ ...m, pin_code: e.target.value.replace(/\D/g, '') }))}
                className={INPUT_CLS} placeholder="123456" autoComplete="one-time-code" />
              <button type="button" className={SIDE_BTN} title="Τυχαίο PIN" aria-label="Τυχαίο PIN"
                onClick={() => setManager(m => ({ ...m, pin_code: generateRandomPin() }))}>
                <Shuffle className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </SheetField>
        </SheetSection>

        <SheetSection id="support-new-billing" title="Χρέωση και εξοπλισμός"
          hint="Η εγκατάσταση είναι και η αρχή του πακέτου. Οι λειτουργίες που χρεώνονται βγαίνουν από ό,τι άνοιξες πιο πάνω.">
          <div className="xs-grid2">
            <SheetField label="Εγκατάσταση" htmlFor="support-new-installed">
              <input id="support-new-installed" type="date" value={billing.installed_at} className={`${INPUT_CLS} dark:[color-scheme:dark]`}
                onChange={(e) => setBilling(b => ({ ...b, installed_at: e.target.value }))} />
            </SheetField>
            <SheetField label="Κύκλος χρέωσης" htmlFor="support-new-cycle">
              <select id="support-new-cycle" className={SELECT_CLS} value={billing.billing_cycle}
                onChange={(e) => setBilling(b => ({ ...b, billing_cycle: e.target.value }))}>
                {CYCLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </SheetField>
            <SheetField label="Συμφωνημένη τιμή ανά κύκλο (€)" htmlFor="support-new-price" hint="Κενό = τη γράφεις αργότερα στις «Χρεώσεις».">
              <input id="support-new-price" inputMode="decimal" value={billing.agreed_price} className={`${INPUT_CLS} tabular-nums`}
                onChange={(e) => setBilling(b => ({ ...b, agreed_price: e.target.value }))} placeholder="π.χ. 700" />
            </SheetField>
          </div>
          <div className="mt-4 space-y-2" data-testid="support-new-hardware">
            {billing.hardware.map((h, i) => {
              const set = (patch: Partial<HwLine>) => setBilling(b => ({ ...b, hardware: b.hardware.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
              return (
                <div key={i} className="grid grid-cols-[minmax(0,1fr)_96px_96px_auto] items-end gap-2">
                  <SheetField label={i === 0 ? 'Εξοπλισμός που παραδόθηκε' : ''} htmlFor={`support-new-hw-${i}`}>
                    <input id={`support-new-hw-${i}`} value={h.item} className={INPUT_CLS} placeholder="π.χ. Ταμειακό PC + οθόνη"
                      aria-label={i === 0 ? undefined : `Εξοπλισμός ${i + 1}`} onChange={(e) => set({ item: e.target.value })} />
                  </SheetField>
                  <SheetField label={i === 0 ? 'Τιμή €' : ''} htmlFor={`support-new-hw-price-${i}`}>
                    <input id={`support-new-hw-price-${i}`} inputMode="decimal" value={h.price} className={`${INPUT_CLS} tabular-nums`}
                      aria-label={`Τιμή: ${h.item || `εξοπλισμός ${i + 1}`}`} onChange={(e) => set({ price: e.target.value })} />
                  </SheetField>
                  <SheetField label={i === 0 ? 'Εξοφλήθηκαν €' : ''} htmlFor={`support-new-hw-paid-${i}`}>
                    <input id={`support-new-hw-paid-${i}`} inputMode="decimal" value={h.paid_amount} className={`${INPUT_CLS} tabular-nums`}
                      aria-label={`Εξοφλήθηκαν: ${h.item || `εξοπλισμός ${i + 1}`}`} onChange={(e) => set({ paid_amount: e.target.value })} />
                  </SheetField>
                  <button type="button" className={SIDE_BTN} aria-label={`Αφαίρεση: ${h.item || `εξοπλισμός ${i + 1}`}`}
                    onClick={() => setBilling(b => ({ ...b, hardware: b.hardware.filter((_, j) => j !== i) }))}>
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            <button type="button" className={GHOST_BTN} data-testid="support-new-hw-add"
              onClick={() => setBilling(b => ({ ...b, hardware: [...b.hardware, { item: '', price: '', paid_amount: '' }] }))}>
              <Plus className="w-4 h-4" aria-hidden="true" />Εξοπλισμός
            </button>
          </div>
        </SheetSection>
      </div>
    </DetailPanel>
  );
};

export default SupportNewStore;
